/**
 * Часы работы и параметры слотов доставки — ЕДИНЫЙ ИСТОЧНИК для сайта.
 *
 * Решение владельца (вопрос 46): «Для слотов будет 12:30–22:15, время работы
 * как и ранее 11:00 до 22:00». То есть две разные величины, и обе верные:
 *   • приём заказов и «как можно быстрее» — 11:00–22:00. Это то, что обещает
 *     FAQ и что уходит в Schema.org для Google;
 *   • слоты предзаказа — от 12:30 до 22:15 (последнее окно 21:45–22:15).
 *     Кухня физически не отдаёт первый заказ в 11:00, поэтому окно доставки
 *     начинается позже открытия.
 *
 * Раньше эти числа лежали россыпью: здесь, в config/site.ts (оттуда в
 * разметку для Google), в генераторе слотов корзины — и расходились. Теперь
 * значение одно, а config/site.ts импортирует его отсюда, а не дублирует.
 * Зависимость строго односторонняя: этот файл НИЧЕГО не импортирует из
 * config/site.ts, иначе получится цикл (и весь SEO-конфиг с сотнями
 * ключевых слов уедет в клиентский бандл корзины).
 *
 * Предзаказ принимается с 06:00 — это не часы работы, а окно, в которое можно
 * оформить заказ на будущий слот.
 */

/** С какого часа принимаются предзаказы на будущие слоты. */
export const PREORDER_HOUR = 6;

/** Открытие: с этого часа можно заказать «как можно быстрее». */
export const OPEN_HOUR = 11;

/** Закрытие: приём заказов прекращается. */
export const CLOSE_HOUR = 22;

/**
 * Границы и шаг слотов предзаказа.
 *
 * `lastEnd` — конец ПОСЛЕДНЕГО окна, а не начало: при окне в 30 минут
 * последний слот получается 21:45–22:15. Именно так их отдаёт приложение,
 * и владелец подтвердил, что это правильные слоты.
 */
export const DELIVERY_SLOTS = {
  firstStart: { hour: 12, minute: 30 },
  lastEnd: { hour: 22, minute: 15 },
  /** Шаг между началами соседних слотов, минуты. */
  stepMinutes: 15,
  /** Длина окна доставки, минуты. */
  windowMinutes: 30,
} as const;

/** Часовой пояс ресторана. Рабочие часы — его, а не устройства клиента. */
const RESTAURANT_TIME_ZONE = 'Europe/Moscow';

/**
 * Текущий час в поясе ресторана.
 *
 * Раньше брали `new Date().getHours()` — часы устройства. У клиента из другого
 * пояса (или со сбитым временем) кухня «закрывалась» в рабочее время и
 * «открывалась» в нерабочее.
 */
function getRestaurantHour(date: Date = new Date()): number {
  try {
    // Тот же приём, что в lib/date-utils.ts (toMoscowTime): Intl + formatToParts.
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: RESTAURANT_TIME_ZONE,
      hour: '2-digit',
      hour12: false,
    }).formatToParts(date);
    const raw = parts.find((p) => p.type === 'hour')?.value;
    const hour = raw ? parseInt(raw, 10) : NaN;
    if (!Number.isNaN(hour)) return hour % 24;
  } catch {
    // Среда без данных о часовых поясах — падать нельзя, считаем по устройству
  }
  return date.getHours();
}

/** Кухня ещё не открылась сегодня (00:00–10:59 по Москве). */
export function isBeforeOpening(date: Date = new Date()): boolean {
  return getRestaurantHour(date) < OPEN_HOUR;
}

/** Can place any order (including immediate delivery) */
export function isRestaurantOpen(): boolean {
  const hour = getRestaurantHour();
  return hour >= OPEN_HOUR && hour < CLOSE_HOUR;
}

/** Can place pre-orders (scheduled delivery) */
export function isPreorderAvailable(): boolean {
  const hour = getRestaurantHour();
  return hour >= PREORDER_HOUR && hour < CLOSE_HOUR;
}

/** Fully closed — no orders at all */
export function isFullyClosed(): boolean {
  return !isPreorderAvailable();
}

export function getWorkingHoursText(): string {
  return `${OPEN_HOUR}:00 — ${CLOSE_HOUR}:00`;
}

export function getPreorderHoursText(): string {
  return `${PREORDER_HOUR}:00 — ${CLOSE_HOUR}:00`;
}

/**
 * Check if delivery point is on technical break
 */
export async function checkTechnicalBreak(deliveryPointId: number): Promise<boolean> {
  try {
    const res = await fetch(`/api/delivery-points/${deliveryPointId}`);
    if (!res.ok) return false;
    const data = await res.json();
    return !!data.technical_break;
  } catch {
    return false;
  }
}
